import { Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { componentsTestIds } from '@components/testids';
import type { ReadmodelRow } from '../readmodelSource';
import { EMPTY_CELL } from './tableData';
import type { ReadmodelWidgetState } from './readmodelState';
import { ReadmodelEmpty, ReadmodelLoading, ReadmodelUnavailable } from './ReadmodelWidgetStates';
import classes from './ListWidget.module.css';

/**
 * TV1-2291 (SDD §5/§6 · t25) — the `list` tile: the rows themselves, not a
 * reduction of them. Recent drift episodes, unanswered questions, orders left to
 * fulfil.
 *
 * ## Why it is not `TableWidget`
 *
 * `table` is Cube-backed and renders `resultSet.tableColumns()` — a real grid with a
 * header row, per-column alignment and an annotation that drives cell formatting. A
 * readmodel row has none of that: it is untyped JSON, and the widget is usually
 * given a quarter of the report's width. So this renders a LIST — one line per row,
 * identity field first — which is legible at 300px where a three-column grid is not.
 *
 * ## Columns
 *
 * `widgetComponentProps.columns` is a list of field paths, in order, and the first is
 * the row's identity. When it is absent the columns are DERIVED from the first row —
 * capped, and with the plumbing fields dropped — so a freshly-placed card shows
 * something legible instead of an empty box while its author is still configuring
 * it. That derivation is a display convenience and never a data decision: it cannot
 * change which rows were fetched.
 */

/**
 * Fields that are never worth a column: tenancy/identity plumbing that is the same
 * on every row, or opaque. Dropped only from the DERIVED column set — an author who
 * explicitly asks for `id` gets `id`.
 */
const DERIVED_COLUMN_BLOCKLIST: ReadonlySet<string> = new Set([
  'id', '_id', 'accountId', 'ownerId', 'organizationId', 'tenantId'
]);

/** How many columns a derived (unconfigured) list shows. Three fits 300px; four does not. */
export const DERIVED_COLUMN_LIMIT = 3;

/**
 * The columns to render: the author's, or a derivation from the first row.
 *
 * Returns `[]` only when there is genuinely nothing to key on (no rows, no
 * configuration) — the caller then renders the empty state rather than a list of
 * blank lines.
 */
export const deriveListColumns = (
  rows: ReadmodelRow[],
  configured: unknown
): string[] => {
  if ( Array.isArray( configured )) {
    const explicit = configured.filter(( entry ): entry is string => typeof entry === 'string' && entry.trim() !== '' );
    if ( explicit.length ) return explicit;
  }

  const first = rows[0];
  if ( !first || typeof first !== 'object' ) return [];

  return Object.keys( first )
    .filter(( key ) => !DERIVED_COLUMN_BLOCKLIST.has( key ))
    .slice( 0, DERIVED_COLUMN_LIMIT );
};

/** Read a (possibly dotted) field path off a row — same traversal `readmodelSource` uses. */
const readField = ( row: ReadmodelRow, field: string ): unknown =>
  field.split( '.' ).reduce<unknown>(
    ( value, segment ) =>
      typeof value === 'object' && value !== null
        ? ( value as Record<string, unknown> )[segment]
        : undefined,
    row
  );

/**
 * One cell, as text.
 *
 * A readmodel row is untyped JSON, so there is no column annotation to format
 * against (this is the concrete cost of not being `TableWidget`). Numbers go
 * through `Intl`; an array is joined; anything object-shaped renders as the EMPTY
 * glyph rather than `[object Object]`, because a reader can act on "no value here"
 * and cannot act on that.
 */
export const formatListCell = ( value: unknown, locale?: string ): string => {
  if ( value === null || value === undefined || value === '' ) return EMPTY_CELL;
  if ( typeof value === 'string' ) return value;
  if ( typeof value === 'boolean' ) return String( value );
  if ( typeof value === 'number' ) {
    return Number.isFinite( value )
      ? new Intl.NumberFormat( locale, { maximumFractionDigits: 2 }).format( value )
      : EMPTY_CELL;
  }
  if ( Array.isArray( value )) {
    const entries = value.filter(( entry ) => entry !== null && entry !== undefined ).map( String );
    return entries.length ? entries.join( ', ' ) : EMPTY_CELL;
  }
  return EMPTY_CELL;
};

export type ListWidgetProps = {
  /** The resolved state — see `readmodelState.resolveReadmodelWidgetState`. */
  state: ReadmodelWidgetState;
  /** `widgetComponentProps.columns` — field paths, identity first. */
  columns?: unknown;
  /** Explicit locale for deterministic tests; defaults to the runtime locale. */
  locale?: string;
};

export const ListWidget = ({ state, columns, locale }: ListWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( state.kind === 'loading' ) return <ReadmodelLoading />;
  if ( state.kind === 'error' || state.kind === 'unavailable' ) return <ReadmodelUnavailable />;
  if ( state.kind === 'empty' ) return <ReadmodelEmpty />;

  const { rows, count } = state;
  const fields = deriveListColumns( rows, columns );

  // `count` can legitimately exceed `rows.length` while `rows` is empty — a
  // `limit: 0` query, or a `-search` collection whose server total outruns the cap.
  // Either way there is nothing to LIST, and the empty state is the honest answer.
  if ( !rows.length || !fields.length ) return <ReadmodelEmpty />;

  // The row cap already ran server-side (or in `applyPlanToRows`); this only says so.
  const hidden = Math.max( 0, count - rows.length );

  return (
    <div className={classes.container} data-testid={componentsTestIds.listWidget} data-readmodel-state={state.kind}>
      <ul className={classes.rows}>
        {rows.map(( row, index ) => (
          // A readmodel row usually HAS an id, but `list` is pointed at arbitrary
          // collections and the field set is author-configured — so the row's
          // position in the server-ordered result is the only identity guaranteed to
          // exist. Same call `TableWidget` makes.
          <li key={index} className={classes.row} data-testid={componentsTestIds.listWidgetRow}>
            <span className={classes.primary}>{formatListCell( readField( row, fields[0]), locale )}</span>
            {fields.slice( 1 ).map(( field ) => (
              <span key={field} className={classes.secondary}>
                {formatListCell( readField( row, field ), locale )}
              </span>
            ))}
          </li>
        ))}
      </ul>

      {hidden > 0 ? (
        <Text component="div" className={classes.truncated} data-testid={componentsTestIds.listWidgetTruncated}>
          {t( 'widget_list_truncated', '+{{count}} more', { count: hidden })}
        </Text>
      ) : null}
    </div>
  );
};
